import api from "../api/client";
import React, { useRef, useState, useEffect } from "react";
import {
  Card, Space, Button, Modal, Descriptions, Typography, Divider,
  Tooltip, Badge, Select, DatePicker, Input, Row, Col, Statistic,
  Alert, Table, Progress, Empty, Spin, Tag,
} from "antd";
import { ProTable } from "@ant-design/pro-table";
import {
  EyeOutlined, WarningOutlined, DownloadOutlined, ReloadOutlined,
  FilterOutlined, ArrowLeftOutlined, ShoppingOutlined, RiseOutlined,
  FallOutlined, PieChartOutlined, LineChartOutlined, TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const speciesMap = {
  weaners: "weaner", goats: "goat", sheep: "sheep",
  pigs: "pig", poultry: "poultry",
};

const fmtKES = (v) =>
  `KES ${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const AnimalMortalityHistory = () => {
  const actionRef = useRef();
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [consumptionData, setConsumptionData] = useState(null);
  const [loadingConsumptions, setLoadingConsumptions] = useState(false);
  const [farms, setFarms] = useState([]);

  const [filters, setFilters] = useState({
    mortality_type: "all", species: "all",
    dateRange: null, farm: "all", search: "",
  });

  const [summary, setSummary] = useState({
    totalMortalityEvents: 0, parentMortalityEvents: 0,
    offspringMortalityEvents: 0, totalLossValue: 0,
    totalPurchaseCost: 0, totalConsumptionCost: 0,
    totalMortalityQuantity: 0, bySpecies: {}, monthlyTrend: [],
  });

  useEffect(() => {
    api.get("/farm")
      .then(({ data }) => setFarms(data || []))
      .catch((e) => console.error("Farms error:", e));
  }, []);

  const fetchSummary = async () => {
    try {
      const params = {};
      if (filters.dateRange?.length === 2) {
        params.startDate = filters.dateRange[0].format("YYYY-MM-DD");
        params.endDate = filters.dateRange[1].format("YYYY-MM-DD");
      }
      if (filters.farm !== "all") params.farm = filters.farm;
      if (filters.species !== "all")
        params.species = speciesMap[filters.species] || filters.species;

      const { data } = await api.get("/animal-mortality/summary", { params });
      setSummary(data);
    } catch (e) {
      console.error("Summary error:", e);
    }
  };

  useEffect(() => { fetchSummary(); }, [filters]);

  const openDetails = async (record) => {
    setSelectedEvent(record);
    setDetailsModal(true);
    setConsumptionData(null);
    setLoadingConsumptions(true);
    try {
      const { data } = await api.get(
        `/animal-mortality/${record.id}/consumptions`
      );
      setConsumptionData(data);
    } catch (e) {
      console.error("Consumptions error:", e);
    } finally {
      setLoadingConsumptions(false);
    }
  };

  const columns = [
    {
      title: "Date", dataIndex: "eventDate", width: 100,
      render: (d) => dayjs(d).format("DD MMM YY"),
      sorter: (a, b) => dayjs(a.eventDate).unix() - dayjs(b.eventDate).unix(),
    },
    {
      title: "Animal / Batch", dataIndex: "spanNumber", width: 160,
      render: (text, r) => (
        <div>
          <Text strong>{text || r.batchId || "N/A"}</Text>
          {r.animalName && (
            <div style={{ fontSize: 11, color: "#888" }}>{r.animalName}</div>
          )}
          {r.isBatchAnimal && (
            <span color="purple" style={{ marginTop: 2, fontSize: 10 }}>
              BATCH · {r.batchSize} birds
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Species", dataIndex: "type_slug", width: 90,
      render: (s) => <span color="blue">{s?.toUpperCase()}</span>,
    },
    {
      title: "Type", dataIndex: "mortalityType", width: 100,
      render: (t) => (
        <span color={t === "parent" ? "orange" : "red"}>
          {t === "parent" ? "Adult" : "Offspring"}
        </span>
      ),
    },
    { title: "Age", dataIndex: "ageAtDeath", width: 70, render: (v) => v || "—" },
    {
      title: "Died", dataIndex: "effectiveQuantity", width: 70, align: "center",
      render: (q, r) => (
        <Tooltip title={r.isBatchAnimal ? `Out of ${r.batchSize} birds in batch` : "Individual"}>
          <Text strong style={{ fontSize: 15 }}>
            {q || 1}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Purchase / Unit", dataIndex: "purchaseCost", width: 130, align: "right",
      render: (v) => <Text type="secondary">{fmtKES(v)}</Text>,
    },
    {
      title: "Consumption / Unit", dataIndex: "consumptionCost", width: 145, align: "right",
      render: (v, r) =>
        v > 0 ? (
          <Tooltip
            title={
              r.isBatchAnimal
                ? "Per bird — total batch consumption ÷ batch size"
                : "Direct animal consumption"
            }
          >
            <Text style={{ color: "#faad14" }}>{fmtKES(v)}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Total Loss", key: "totalLoss", width: 170, align: "right",
      sorter: (a, b) => (a.totalMortalityValue || 0) - (b.totalMortalityValue || 0),
      render: (_, r) => {
        const per = r.perAnimalValue ?? 0;
        const qty = r.effectiveQuantity ?? 1;
        return (
          <div style={{ textAlign: "right" }}>
            <Text strong style={{ fontSize: 14 }}>
              {fmtKES(r.totalMortalityValue ?? per * qty)}
            </Text>
            {qty > 1 && (
              <div style={{ fontSize: 11, color: "#999" }}>
                {qty} × {fmtKES(per)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Farm", dataIndex: "farmName", width: 120, ellipsis: true,
      render: (v, r) => v || r.farmId || "—",
    },
    { title: "By", dataIndex: "userName", width: 100, ellipsis: true },
    {
      title: "", key: "actions", width: 50, fixed: "right",
      render: (_, r) => (
        <Tooltip title="View Details">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetails(r)} />
        </Tooltip>
      ),
    },
  ];

  const request = async (params, sort) => {
    try {
      const q = {};
      if (filters.mortality_type !== "all") q.mortalityType = filters.mortality_type;
      if (filters.species !== "all") q.species = speciesMap[filters.species] || filters.species;
      if (filters.farm !== "all") q.farm = filters.farm;
      if (filters.dateRange?.length === 2) {
        q.startDate = filters.dateRange[0].format("YYYY-MM-DD");
        q.endDate = filters.dateRange[1].format("YYYY-MM-DD");
      }
      if (filters.search) q.search = filters.search;
      if (sort && Object.keys(sort).length > 0) {
        const f = Object.keys(sort)[0];
        q.sort = `${f}:${sort[f] === "ascend" ? "ASC" : "DESC"}`;
      }
      q.page = params.current || 1;
      q.limit = params.pageSize || 20;

      const { data } = await api.get("/animal-mortality/list", { params: q });
      return { data: data.data, success: true, total: data.pagination?.total || 0 };
    } catch (e) {
      console.error("List error:", e);
      return { data: [], success: false, total: 0 };
    }
  };

  const exportToCSV = async () => {
    try {
      const q = {};
      if (filters.mortality_type !== "all") q.mortalityType = filters.mortality_type;
      if (filters.species !== "all") q.species = speciesMap[filters.species] || filters.species;
      if (filters.farm !== "all") q.farm = filters.farm;
      if (filters.dateRange?.length === 2) {
        q.startDate = filters.dateRange[0].format("YYYY-MM-DD");
        q.endDate = filters.dateRange[1].format("YYYY-MM-DD");
      }
      const res = await api.get("/animal-mortality/export", { params: q, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `mortality_${dayjs().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Export error:", e); }
  };

  const resetFilters = () => {
    setFilters({
      mortality_type: "all", species: "all",
      dateRange: null, farm: "all", search: "",
    });
    actionRef.current?.reload();
  };

  const consumptionPct =
    summary.totalLossValue > 0
      ? (summary.totalConsumptionCost / summary.totalLossValue) * 100
      : 0;
  const purchasePct = 100 - consumptionPct;

  return (
    <Card
      title={
        <Space>
          <WarningOutlined style={{ color: "#ff4d4f" }} />
          <span>Animal Mortality History</span>
          <Badge count={summary.totalMortalityEvents} showZero color="red" />
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchSummary(); actionRef.current?.reload(); }}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportToCSV}>
            Export CSV
          </Button>
          <Link to="/animal-assets">
            <Button type="primary" icon={<ArrowLeftOutlined />}>Back</Button>
          </Link>
        </Space>
      }
    >
      {/* HERO SUMMARY */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            size="small"
            // style={{
            //   background: "linear-gradient(135deg, #fff1f0 0%, #ffe7e5 100%)",
            //   borderLeft: "4px solid #cf1322",
            // }}
          >
            <Row align="middle">
              <Col flex="auto">
                <Statistic
                  title={<Text strong>Total Loss Value</Text>}
                  value={summary.totalLossValue || 0}
                  precision={2}
                  prefix="KES"
                  valueStyle={{ fontSize: 32, fontWeight: 700 }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {summary.totalMortalityEvents} events · {summary.totalMortalityQuantity} animals
                </Text>
              </Col>
              <Col>
                <FallOutlined style={{ fontSize: 48, opacity: 0.25 }} />
              </Col>
            </Row>

            <Divider style={{ margin: "12px 0" }} />

            <Row gutter={8}>
              <Col span={12}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <ShoppingOutlined style={{ color: "#1677ff" }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>Purchase</Text>
                </div>
                <Text strong style={{ color: "#1677ff", fontSize: 16 }}>
                  {fmtKES(summary.totalPurchaseCost)}
                </Text>
              </Col>
              <Col span={12}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <RiseOutlined style={{ color: "#faad14" }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>Consumption</Text>
                </div>
                <Text strong style={{ color: "#faad14", fontSize: 16 }}>
                  {fmtKES(summary.totalConsumptionCost)}
                </Text>
              </Col>
            </Row>

            <div style={{ marginTop: 8 }}>
              <Progress
                percent={100}
                success={{ percent: purchasePct, strokeColor: "#1677ff" }}
                strokeColor="#faad14"
                showInfo={false}
                size="small"
              />
              <div
                style={{
                  display: "flex", justifyContent: "space-between",
                  fontSize: 11, color: "#888", marginTop: 2,
                }}
              >
                <span>{purchasePct.toFixed(1)}% Purchase</span>
                <span>{consumptionPct.toFixed(1)}% Consumption</span>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Row gutter={[12, 12]}>
            <Col xs={12}>
              <Card size="small">
                <Statistic
                  title="Total Events"
                  value={summary.totalMortalityEvents || 0}
                  valueStyle={{ fontSize: 22 }}
                  prefix={<WarningOutlined />}
                />
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  Adult: <b>{summary.parentMortalityEvents}</b> · Offspring:{" "}
                  <b>{summary.offspringMortalityEvents}</b>
                </div>
              </Card>
            </Col>
            <Col xs={12}>
              <Card size="small">
                <Statistic
                  title="Animals Lost"
                  value={summary.totalMortalityQuantity || 0}
                  valueStyle={{ fontSize: 22 }}
                />
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  Avg:{" "}
                  {summary.totalMortalityQuantity
                    ? fmtKES(summary.totalLossValue / summary.totalMortalityQuantity)
                    : fmtKES(0)}{" "}
                  / animal
                </div>
              </Card>
            </Col>
            <Col xs={12}>
              <Card size="small">
                <Statistic
                  title="Purchase Cost"
                  value={summary.totalPurchaseCost || 0}
                  precision={2}
                  prefix="KES"
                  valueStyle={{ color: "#1677ff", fontSize: 18 }}
                />
              </Card>
            </Col>
            <Col xs={12}>
              <Card size="small">
                <Statistic
                  title="Consumption Cost"
                  value={summary.totalConsumptionCost || 0}
                  precision={2}
                  prefix="KES"
                  valueStyle={{ color: "#faad14", fontSize: 18 }}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* BY SPECIES */}
      {Object.keys(summary.bySpecies || {}).length > 0 && (
        <Card
          size="small"
          title={<Space><PieChartOutlined /><span>Breakdown by Species</span></Space>}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={[12, 12]}>
            {Object.entries(summary.bySpecies).map(([species, data]) => {
              const sp = data.value || 0;
              const pPct = sp > 0 ? ((data.purchaseCost || 0) / sp) * 100 : 0;
              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={species}>
                  <Card size="small" style={{ height: "100%" }}>
                    <div
                      style={{
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginBottom: 8,
                      }}
                    >
                      <span color="blue" style={{ margin: 0 }}>
                        {species.toUpperCase()}
                      </span>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {data.count} events
                      </Text>
                    </div>
                    <Text strong style={{ fontSize: 20,  }}>
                      {fmtKES(sp)}
                    </Text>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                      {data.quantity} animals lost
                    </div>
                    <Divider style={{ margin: "8px 0" }} />
                    <div style={{ fontSize: 12 }}>
                      Purchase: <b>{fmtKES(data.purchaseCost)}</b>
                    </div>
                    <div style={{ fontSize: 12 }}>
                      Consumption: <b>{fmtKES(data.consumptionCost)}</b>
                    </div>
                    <Progress
                      percent={100}
                      success={{ percent: pPct, strokeColor: "#1677ff" }}
                      strokeColor="#faad14"
                      showInfo={false}
                      size="small"
                      style={{ marginTop: 6 }}
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}

      {/* MONTHLY TREND */}
      {summary.monthlyTrend?.length > 1 && (
        <Card
          size="small"
          title={<Space><LineChartOutlined /><span>Monthly Trend</span></Space>}
          style={{ marginBottom: 16 }}
        >
          <div
            style={{
              display: "flex", alignItems: "flex-end",
              gap: 8, height: 120, padding: "8px 0",
            }}
          >
            {summary.monthlyTrend.map((m) => {
              const max = Math.max(...summary.monthlyTrend.map((x) => x.value));
              const height = max > 0 ? (m.value / max) * 100 : 0;
              return (
                <Tooltip
                  key={m.month}
                  title={`${m.month}: ${fmtKES(m.value)} · ${m.quantity} animals`}
                >
                  <div
                    style={{
                      flex: 1, display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 4,
                    }}
                  >
                    <div
                      style={{
                        width: "100%", height: `${height}%`, minHeight: 4,
                        background: "linear-gradient(180deg, #75ff75 0%, #13cf52 100%)",
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                    <div style={{ fontSize: 10, color: "#888" }}>
                      {m.month.slice(5)}
                    </div>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </Card>
      )}

      {/* FILTERS */}
      <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Input.Search
          placeholder="Search by span number..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          onSearch={(v) => {
            setFilters({ ...filters, search: v });
            actionRef.current?.setPageInfo({ current: 1 });
            actionRef.current?.reload();
          }}
          style={{ width: 250 }}
          allowClear
        />

        <Select
          value={filters.mortality_type}
          onChange={(v) => { setFilters({ ...filters, mortality_type: v }); actionRef.current?.reload(); }}
          style={{ width: 160 }}
        >
          <Option value="all">All Types</Option>
          <Option value="parent">Adult Mortality</Option>
          <Option value="offspring">Offspring Mortality</Option>
        </Select>

        <Select
          value={filters.species}
          onChange={(v) => { setFilters({ ...filters, species: v }); actionRef.current?.reload(); }}
          style={{ width: 130 }}
        >
          <Option value="all">All Species</Option>
          <Option value="goat">Goat</Option>
          <Option value="sheep">Sheep</Option>
          <Option value="pig">Pig</Option>
          <Option value="poultry">Poultry</Option>
          <Option value="weaners">Weaners</Option>
        </Select>

        <Select
          value={filters.farm}
          onChange={(v) => { setFilters({ ...filters, farm: v }); actionRef.current?.reload(); }}
          style={{ width: 160 }}
        >
          <Option value="all">All Farms</Option>
          {farms.map((f) => (
            <Option key={f.id} value={f.id}>{f.name}</Option>
          ))}
        </Select>

        <RangePicker
          value={filters.dateRange}
          onChange={(d) => { setFilters({ ...filters, dateRange: d }); actionRef.current?.reload(); }}
          style={{ width: 250 }}
        />

        <Button icon={<FilterOutlined />} onClick={resetFilters}>
          Reset
        </Button>
      </div>

      {/* TABLE */}
      <ProTable
        actionRef={actionRef}
        defaultSize="small"
        columns={columns}
        request={request}
        rowKey="id"
        search={false}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        scroll={{ x: 1500 }}
        options={{ reload: true, density: true, setting: true }}
      />

      {/* DETAILS MODAL */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: "#ff4d4f" }} />
            <span>Mortality Event #{selectedEvent?.id}</span>
            {selectedEvent?.isBatchAnimal && (
              <span color="purple"><TeamOutlined /> BATCH</span>
            )}
          </Space>
        }
        open={detailsModal}
        onCancel={() => {
          setDetailsModal(false);
          setSelectedEvent(null);
          setConsumptionData(null);
        }}
        footer={[<Button key="close" onClick={() => setDetailsModal(false)}>Close</Button>]}
        width={850}
      >
        {selectedEvent && (
          <>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #fff1f0 0%, #ffe7e5 100%)",
                marginBottom: 16,
              }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Purchase / {selectedEvent.isBatchAnimal ? "Bird" : "Animal"}
                  </Text>
                  <div style={{ color: "#1677ff", fontSize: 18, fontWeight: 600 }}>
                    {fmtKES(selectedEvent.purchaseCost)}
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Consumption / {selectedEvent.isBatchAnimal ? "Bird" : "Animal"}
                  </Text>
                  <div style={{ color: "#faad14", fontSize: 18, fontWeight: 600 }}>
                    {fmtKES(selectedEvent.consumptionCost)}
                  </div>
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Total Loss ({selectedEvent.effectiveQuantity} × per-animal)
                  </Text>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>
                    {fmtKES(selectedEvent.totalMortalityValue)}
                  </div>
                </Col>
              </Row>

              {selectedEvent.isBatchAnimal && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                  <TeamOutlined /> Batch of <b>{selectedEvent.batchSize}</b> birds ·{" "}
                  {selectedEvent.effectiveQuantity} died in this event
                </div>
              )}
            </Card>

            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Date" span={2}>
                {dayjs(selectedEvent.eventDate).format("DD MMM YYYY, h:mm A")}
              </Descriptions.Item>
              <Descriptions.Item label="span / Batch">
                <Text strong color="red">
                  {selectedEvent.spanNumber || selectedEvent.batchId || "N/A"}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Species">
                <span color="blue">{selectedEvent.type_slug?.toUpperCase()}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Mortality Type" span={2}>
                <span color={selectedEvent.mortalityType === "parent" ? "orange" : "red"}>
                  {selectedEvent.mortalityType === "parent"
                    ? "Adult Mortality"
                    : "Offspring Mortality"}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Died in This Event">
                <Text strong style={{  }}>
                  {selectedEvent.effectiveQuantity}
                </Text>
              </Descriptions.Item>
              {selectedEvent.isBatchAnimal && (
                <Descriptions.Item label="Batch Size (original)">
                  {selectedEvent.batchSize} birds
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Age at Death">
                {selectedEvent.ageAtDeath || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Farm">
                {selectedEvent.farmName || selectedEvent.farmId || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Recorded By">
                {selectedEvent.userName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Remarks" span={2}>
                {selectedEvent.remarks || "—"}
              </Descriptions.Item>
            </Descriptions>

            {selectedEvent.isBatchAnimal && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message="How this loss was calculated"
                description={
                  <div style={{ fontSize: 13 }}>
                    <div>
                      Total batch purchase ÷ batch size ={" "}
                      <b>{fmtKES(selectedEvent.purchaseCost)}</b> per bird
                    </div>
                    <div>
                      Total batch consumption ÷ batch size ={" "}
                      <b>{fmtKES(selectedEvent.consumptionCost)}</b> per bird
                    </div>
                    <Divider style={{ margin: "6px 0" }} />
                    <div>
                      Per-bird value = <b>{fmtKES(selectedEvent.perAnimalValue)}</b>
                    </div>
                    <div>
                      Loss for <b>{selectedEvent.effectiveQuantity}</b> bird(s) ={" "}
                      <b style={{  }}>
                        {fmtKES(selectedEvent.totalMortalityValue)}
                      </b>
                    </div>
                  </div>
                }
              />
            )}

            <Divider orientation="left">
              <Space>
                <RiseOutlined style={{ color: "#faad14" }} />
                <span>
                  {selectedEvent.isBatchAnimal
                    ? "Batch Consumption Records"
                    : "Consumption History"}
                </span>
              </Space>
            </Divider>

            {loadingConsumptions ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <Spin />
              </div>
            ) : !consumptionData?.consumptions?.length ? (
              <Empty
                description="No consumption records found for this animal"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Table
                size="small"
                rowKey={(r) => `${r.source}-${r.id}`}
                pagination={false}
                dataSource={consumptionData.consumptions}
                columns={[
                  {
                    title: "Date", dataIndex: "date", width: 100,
                    render: (d) => dayjs(d).format("DD MMM YY"),
                  },
                  { title: "Category", dataIndex: "category" },
                  { title: "Qty", dataIndex: "quantity" },
                  {
                    title: "Unit Cost", dataIndex: "unit_cost", align: "right",
                    render: (v) => fmtKES(v),
                  },
                  {
                    title: "Total", dataIndex: "total_cost", align: "right",
                    render: (v) => (
                      <Text strong style={{ color: "#faad14" }}>{fmtKES(v)}</Text>
                    ),
                  },
                  {
                    title: "Source", dataIndex: "source",
                    render: (s) => (
                      <span color={s === "silo" ? "purple" : "green"}>
                        {s === "silo" ? "Silo/Batch" : "Animal"}
                      </span>
                    ),
                  },
                  { title: "By", dataIndex: "userName" },
                ]}
                footer={() => (
                  <div style={{ textAlign: "right", fontSize: 13 }}>
                    <div>
                      <Text>Total batch consumption: </Text>
                      <Text strong style={{ color: "#faad14" }}>
                        {fmtKES(consumptionData.totalConsumption)}
                      </Text>
                    </div>
                    {consumptionData.isBatch && (
                      <div>
                        <Text>÷ {consumptionData.batchSize} birds = </Text>
                        <Text strong style={{ color: "#faad14" }}>
                          {fmtKES(consumptionData.perAnimalConsumption)} per bird
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              />
            )}
          </>
        )}
      </Modal>
    </Card>
  );
};

export default AnimalMortalityHistory;